export const AI_LIST_ROUTE = '/agent?tab=decisions';
const STORAGE_KEY = 'fm_decision_list_view_v1';
const MAX_AGE_MS = 30 * 60 * 1000;

export interface DecisionListView {
  userId: string;
  entryKey: string;
  url: string;
  scrollY: number;
  anchorId: string;
  anchorOffset: number;
  savedAt: number;
  historyIndex: number;
}

export function validDecisionListView(value: unknown, userId?: string | null): DecisionListView | null {
  if (!value || typeof value !== 'object' || !userId) return null;
  const view = value as DecisionListView;
  if (view.userId !== userId || typeof view.url !== 'string' || !view.url.startsWith('/')) return null;
  let url: URL;
  try { url = new URL(view.url, 'https://local.invalid'); } catch { return null; }
  if (url.origin !== 'https://local.invalid' || url.hash) return null;
  const agentList = url.pathname === '/agent' && url.searchParams.get('tab') === 'decisions';
  const standaloneList = url.pathname === '/ai-decisions';
  if (!agentList && !standaloneList) return null;
  const allowedKeys = agentList ? ['tab', 'decisionFilter'] : ['page'];
  if ([...url.searchParams.keys()].some((key) => !allowedKeys.includes(key))) return null;
  if (agentList && !['all', 'following', '24h'].includes(url.searchParams.get('decisionFilter') || 'all')) return null;
  if (standaloneList && !/^[1-9]\d{0,3}$/.test(url.searchParams.get('page') || '1')) return null;
  if (typeof view.entryKey !== 'string' || typeof view.anchorId !== 'string'
    || !Number.isFinite(view.scrollY) || view.scrollY < 0 || !Number.isFinite(view.anchorOffset)
    || !Number.isInteger(view.historyIndex) || !Number.isFinite(view.savedAt)
    || view.savedAt > Date.now() || Date.now() - view.savedAt > MAX_AGE_MS) return null;
  return view;
}

export function saveDecisionListView(userId: string, entryKey: string, url: string, anchorId: string) {
  const anchor = document.getElementById(`decision-${anchorId}`);
  const view: DecisionListView = {
    userId, entryKey, url, anchorId, scrollY: window.scrollY,
    anchorOffset: anchor?.getBoundingClientRect().top || 0,
    savedAt: Date.now(), historyIndex: window.history.state?.idx ?? -1,
  };
  if (validDecisionListView(view, userId)) {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(view)); } catch { /* Navigation still works without storage. */ }
  }
  return view;
}

export function readDecisionListView(userId?: string | null) {
  try {
    const view = validDecisionListView(JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null'), userId);
    if (!view) sessionStorage.removeItem(STORAGE_KEY);
    return view;
  } catch { return null; }
}

export function restoreDecisionListView(view: DecisionListView) {
  const anchor = document.getElementById(`decision-${view.anchorId}`);
  window.scrollTo({
    top: anchor ? Math.max(0, window.scrollY + anchor.getBoundingClientRect().top - view.anchorOffset) : view.scrollY,
    behavior: 'instant',
  });
}
